import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
  Background,
  BaseEdge,
  Controls,
  EdgeLabelRenderer,
  Handle,
  MarkerType,
  MiniMap,
  Position,
  getBezierPath,
  type Connection,
  type Edge,
  type EdgeProps,
  type Node,
  type NodeChange,
  type NodeProps,
  type ReactFlowInstance,
  type XYPosition,
  useNodesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Bot, Calculator, ChevronLeft, ChevronRight, Columns3, Database, GitBranch, Link2, Plus, RotateCcw, Sparkles, WandSparkles } from 'lucide-react';
import type { StructuredAssetAllowlistRow, WorkspaceOntology } from './structuredAnalyticsConfig';
import {
  buildOntologyGraphModel,
  buildOntologyOverviewProjection,
  layoutOntologyGraph,
  ontologyCatalogLinkStatusLabel,
  type OntologyGraphEdge,
  type OntologyGraphNode,
} from './ontologyGraph';
import { Badge, Button, Card } from '../ui';
import { cn } from '../../shared/lib/cn';

type Selection =
  | { kind: 'entity'; id: string }
  | { kind: 'businessMeaning'; id: string }
  | { kind: 'metric'; id: string }
  | { kind: 'join'; id: string }
  | null;

interface OntologyNodeData extends OntologyGraphNode {
  detailLevel: GraphDetailLevel;
  isSelected: boolean;
  isConnectedToSelection: boolean;
  isDimmed: boolean;
  selectedMetricId?: string;
  onSelectMetric: (metricId: string) => void;
  onSelectBusinessMeaning: (businessMeaningId: string) => void;
}

interface OntologyEdgeData extends OntologyGraphEdge {
  isSelected: boolean;
  isHovered: boolean;
  isDimmed: boolean;
  isOverview: boolean;
  showLabel: boolean;
}

export interface OntologyGraphCanvasProps {
  ontology: WorkspaceOntology;
  allowlist: StructuredAssetAllowlistRow[];
  selection: Selection;
  onSelect: (selection: Selection) => void;
  onCreateEntity?: () => void;
  onCreateMetric?: () => void;
  onCreateJoin?: () => void;
  onCreateJoinBetweenEntities?: (sourceEntityId: string, targetEntityId: string) => void;
  layoutStorageKey?: string;
}

const nodeTypes = {
  ontologyEntity: OntologyEntityNode,
};

const edgeTypes = {
  ontologyRelationship: OntologyRelationshipEdge,
};

type StoredLayout = Record<string, XYPosition>;
type GraphDetailLevel = 'overview' | 'entities' | 'details';

const GRAPH_DETAIL_LEVELS: Array<{
  value: GraphDetailLevel;
  label: string;
  description: string;
}> = [
  { value: 'overview', label: 'Overview', description: 'Topology and entity names' },
  { value: 'entities', label: 'Entities', description: 'Sources and semantic counts' },
  { value: 'details', label: 'Details', description: 'Descriptions, meanings, and metrics' },
];

function defaultGraphDetailLevel(nodeCount: number): GraphDetailLevel {
  if (nodeCount >= 24) return 'overview';
  if (nodeCount >= 12) return 'entities';
  return 'details';
}

function OntologyEntityNode({ data }: NodeProps<OntologyNodeData>) {
  const isOverview = data.detailLevel === 'overview';
  const isCondensed = data.detailLevel === 'entities';
  const catalogTone =
    data.catalogLinkStatus === 'linked'
      ? 'success'
      : data.catalogLinkStatus === 'partial'
        ? 'warning'
        : data.catalogLinkStatus === 'invalid'
          ? 'danger'
          : 'secondary';

  return (
    <div
      className={cn(
        'rounded-lg border bg-white shadow-sm transition-[width,padding,border-color,opacity] dark:bg-dark-surface',
        isOverview ? 'w-[148px] p-2' : isCondensed ? 'w-[184px] p-2.5' : 'w-[286px] p-4',
        data.isSelected
          ? 'border-eliza-red/50 ring-2 ring-eliza-red/20'
          : data.isConnectedToSelection
            ? 'border-eliza-red/30 ring-1 ring-eliza-red/10'
            : data.isDimmed
              ? 'border-gray-100 opacity-55 dark:border-dark-border'
          : 'border-gray-200 hover:border-gray-300 dark:border-dark-border',
      )}
      data-density={data.detailLevel === 'overview' ? 'overview' : isCondensed ? 'condensed' : 'comfortable'}
    >
      <Handle type="target" position={Position.Left} className="!h-2.5 !w-2.5 !bg-gray-400" />
      <Handle type="source" position={Position.Right} className="!h-2.5 !w-2.5 !bg-gray-400" />

      <div className="flex min-w-0 items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-gray-500 dark:bg-dark-surface-2 dark:text-gray-400">
            Entity
          </span>
        </div>
        <Badge variant={catalogTone}>{ontologyCatalogLinkStatusLabel(data.catalogLinkStatus)}</Badge>
      </div>

      <div className={cn('flex items-start gap-2', isOverview ? 'mt-1.5' : isCondensed ? 'mt-2' : 'mt-3')}>
        <span className={cn(
          'flex shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-600 dark:bg-dark-surface-2 dark:text-gray-300',
          isOverview ? 'h-5 w-5' : isCondensed ? 'h-6 w-6' : 'h-8 w-8',
        )}>
          <Database className={isOverview ? 'h-3 w-3' : isCondensed ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
        </span>
        <div className="min-w-0">
          <p className={cn(
            'font-semibold text-charcoal dark:text-gray-100',
            isOverview
              ? 'line-clamp-2 min-h-8 text-[11px] leading-4'
              : isCondensed
                ? 'line-clamp-2 min-h-8 text-sm leading-4'
                : 'truncate text-base leading-5',
          )}>{data.label}</p>
          {!isOverview && !isCondensed ? (
            <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">Logical business entity</p>
          ) : null}
        </div>
      </div>

      {isOverview ? (
        <div className="mt-1.5 flex items-center justify-between border-t border-gray-100 pt-1.5 text-[9px] text-gray-500 dark:border-dark-border dark:text-gray-400">
          <span>
            {(data.groupedEntityCount ?? 1) > 1
              ? `${data.groupedEntityCount} concepts`
              : `${data.bindingCount} source${data.bindingCount === 1 ? '' : 's'}`}
          </span>
          <span>{data.relationshipCount} link{data.relationshipCount === 1 ? '' : 's'}</span>
        </div>
      ) : isCondensed ? (
        <>
          <p className="mt-1.5 truncate font-mono text-[9px] text-gray-500 dark:text-gray-400">{data.assetLabel}</p>
          <div className="mt-1.5 grid grid-cols-4 gap-1 border-t border-gray-100 pt-1.5 text-center dark:border-dark-border">
            <NodeStat label="Sources" value={data.bindingCount} compact />
            <NodeStat label="Fields" value={data.semanticMappingCount} compact />
            <NodeStat label="Metrics" value={data.metrics.length} compact />
            <NodeStat label="Links" value={data.relationshipCount} compact />
          </div>
        </>
      ) : (
        <>
          <p className="mt-3 line-clamp-2 min-h-[34px] text-xs text-gray-500 dark:text-gray-400">
            {data.description || 'Description required before activation review.'}
          </p>

          <div className="mt-3 rounded-md border border-gray-100 bg-gray-50 px-2.5 py-2 dark:border-dark-border dark:bg-dark-surface-2">
        <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500">
          <Link2 className="h-3 w-3" />
          Backed by {data.bindingCount} source{data.bindingCount === 1 ? '' : 's'}
        </div>
        <p className="mt-1 truncate font-mono text-[11px] text-gray-600 dark:text-gray-300">{data.assetLabel}</p>
          </div>

          <div className="mt-3 border-t border-gray-100 pt-3 dark:border-dark-border">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500">
            <Columns3 className="h-3 w-3" />
            Business meanings
          </div>
          {data.businessMeanings.length > 4 ? (
            <span className="text-[10px] text-gray-400 dark:text-gray-500">+{data.businessMeanings.length - 4}</span>
          ) : null}
        </div>
        <div className="mt-2 grid gap-1.5">
          {data.businessMeanings.length === 0 ? (
            <span className="rounded-md border border-dashed border-gray-200 px-2 py-1 text-[10px] text-gray-400 dark:border-dark-border">
              No business meanings
            </span>
          ) : (
            data.businessMeanings.slice(0, 4).map((meaning) => (
              <button
                key={meaning.id}
                type="button"
                className="nodrag nopan flex min-w-0 items-center justify-between gap-2 rounded-md bg-gray-50 px-2 py-1.5 text-left transition-colors hover:bg-gray-100 dark:bg-dark-surface-2 dark:hover:bg-dark-surface"
                onClick={(event) => {
                  event.stopPropagation();
                  data.onSelectBusinessMeaning(meaning.id);
                }}
                title={meaning.conceptKey}
              >
                <span className="min-w-0 truncate text-[11px] font-medium text-charcoal dark:text-gray-100">
                  {meaning.label}
                </span>
                <span className="shrink-0 rounded-full bg-white px-1.5 py-0.5 text-[9px] text-gray-500 dark:bg-dark-surface dark:text-gray-400">
                  {meaning.valueKind || meaning.defaultAggregation || 'field'}
                </span>
              </button>
            ))
          )}
        </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-1.5">
        {data.metrics.length === 0 ? (
          <span className="rounded-full border border-dashed border-gray-200 px-2 py-1 text-[10px] text-gray-400 dark:border-dark-border">
            No metrics
          </span>
        ) : (
          data.metrics.slice(0, 4).map((metric) => (
            <button
              key={metric.id}
              type="button"
              className={cn(
                'nodrag nopan inline-flex max-w-full items-center gap-1 rounded-full px-2 py-1 text-[10px] font-medium transition-colors',
                data.selectedMetricId === metric.id
                  ? 'bg-eliza-red text-white'
                  : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-300',
              )}
              onClick={(event) => {
                event.stopPropagation();
                data.onSelectMetric(metric.id);
              }}
            >
              <Calculator className="h-3 w-3 shrink-0" />
              <span className="truncate">{metric.name}</span>
            </button>
          ))
        )}
        {data.metrics.length > 4 && (
          <span className="rounded-full bg-gray-100 px-2 py-1 text-[10px] text-gray-500 dark:bg-dark-surface-2 dark:text-gray-300">
            +{data.metrics.length - 4}
          </span>
        )}
          </div>

          <div className="mt-4 grid grid-cols-4 gap-2 border-t border-gray-100 pt-3 text-center dark:border-dark-border">
            <NodeStat label="Aliases" value={data.synonymCount} />
            <NodeStat label="Meanings" value={data.semanticMappingCount} />
            <NodeStat label="Metrics" value={data.metrics.length} />
            <NodeStat label="Relations" value={data.relationshipCount} />
          </div>
        </>
      )}
    </div>
  );
}

function OntologyRelationshipEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  data,
}: EdgeProps<OntologyEdgeData>) {
  const [path, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    curvature: 0.28,
  });
  const selected = Boolean(data?.isSelected);
  const hovered = Boolean(data?.isHovered);
  const dimmed = Boolean(data?.isDimmed);
  const overview = Boolean(data?.isOverview);
  const stroke = selected || hovered ? '#C53D3D' : '#8B95A1';
  const semanticOnly = data?.relationshipKind === 'semantic_only';
  const needsEvidence = !data?.traversalAllowed && !semanticOnly;

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        markerEnd={overview && !selected && !hovered ? undefined : markerEnd}
        style={{
          stroke,
          strokeWidth: selected ? 3 : hovered ? 2.5 : overview ? 1.1 : 1.8,
          strokeDasharray: semanticOnly ? '2 5' : needsEvidence ? '6 5' : undefined,
          opacity: dimmed ? 0.15 : overview && !selected && !hovered ? 0.34 : 1,
        }}
      />
      {(data?.showLabel || selected || hovered) ? (
        <EdgeLabelRenderer>
          <div
            className="pointer-events-none absolute max-w-[230px] -translate-x-1/2 -translate-y-1/2"
            style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}
          >
            <div
              className={cn(
                'rounded-md border bg-white px-2.5 py-1.5 text-center shadow-sm dark:bg-dark-surface',
                selected || hovered
                  ? 'border-eliza-red/40 text-eliza-red'
                  : 'border-gray-200 text-gray-600 dark:border-dark-border dark:text-gray-300',
                dimmed && 'opacity-40',
              )}
            >
              <p className="truncate text-[11px] font-semibold leading-4">{data?.label ?? 'Relationship'}</p>
              <div className="mt-1 flex max-w-full justify-center gap-1">
                {data?.cardinalityLabel ? <RelationshipChip>{data.cardinalityLabel}</RelationshipChip> : null}
                {data?.reviewStatusLabel ? (
                  <RelationshipChip tone={data.reviewStatusTone}>{data.reviewStatusLabel}</RelationshipChip>
                ) : null}
              </div>
            </div>
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
}

function RelationshipChip({
  children,
  tone = 'neutral',
}: {
  children: React.ReactNode;
  tone?: OntologyGraphEdge['reviewStatusTone'];
}) {
  return (
    <span
      className={cn(
        'inline-flex max-w-[96px] items-center justify-center rounded-full px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.08em]',
        tone === 'success' && 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300',
        tone === 'warning' && 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
        tone === 'danger' && 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300',
        tone === 'neutral' && 'bg-gray-100 text-gray-500 dark:bg-dark-surface-2 dark:text-gray-300',
      )}
    >
      <span className="truncate">{children}</span>
    </span>
  );
}

function NodeStat({ label, value, compact = false }: { label: string; value: number; compact?: boolean }) {
  return (
    <div>
      <p className={cn('font-semibold text-charcoal dark:text-gray-100', compact ? 'text-xs' : 'text-sm')}>{value}</p>
      <p className={cn('text-gray-500 dark:text-gray-400', compact ? 'text-[8px]' : 'text-[10px]')}>{label}</p>
    </div>
  );
}

export function OntologyGraphCanvas({
  ontology,
  allowlist,
  selection,
  onSelect,
  onCreateEntity,
  onCreateMetric,
  onCreateJoin,
  onCreateJoinBetweenEntities,
  layoutStorageKey,
}: OntologyGraphCanvasProps) {
  const model = useMemo(
    () => buildOntologyGraphModel(ontology, allowlist),
    [allowlist, ontology],
  );
  const unconnectedEntityCount = model.nodes.filter(
    (node) => node.relationshipCount === 0,
  ).length;
  const canFocusConnectedGraph = model.edges.length > 0 && unconnectedEntityCount > 0;
  const [showUnconnectedEntities, setShowUnconnectedEntities] = useState(false);
  const visibleGraphNodes = useMemo(
    () =>
      canFocusConnectedGraph && !showUnconnectedEntities
        ? model.nodes.filter((node) => node.relationshipCount > 0)
        : model.nodes,
    [canFocusConnectedGraph, model.nodes, showUnconnectedEntities],
  );
  const [hoveredJoinId, setHoveredJoinId] = useState<string | null>(null);
  const [isSummaryCollapsed, setIsSummaryCollapsed] = useState(
    () => model.nodes.length >= 24,
  );
  const [isRelationshipNavigatorCollapsed, setIsRelationshipNavigatorCollapsed] = useState(
    () => model.edges.length >= 24,
  );
  const [detailLevel, setDetailLevel] = useState<GraphDetailLevel>(
    () => defaultGraphDetailLevel(model.nodes.length),
  );
  const graphProjection = useMemo(
    () =>
      detailLevel === 'overview'
        ? buildOntologyOverviewProjection(visibleGraphNodes, model.edges)
        : { nodes: visibleGraphNodes, edges: model.edges },
    [detailLevel, model.edges, visibleGraphNodes],
  );
  const [flowInstance, setFlowInstance] = useState<ReactFlowInstance | null>(null);
  const activeLayoutStorageKey = layoutStorageKey && detailLevel !== 'details'
    ? `${layoutStorageKey}:${detailLevel}`
    : layoutStorageKey;
  const [manualPositions, setManualPositions] = useState<StoredLayout>(() =>
    readStoredLayout(activeLayoutStorageKey),
  );

  useEffect(() => {
    setManualPositions(readStoredLayout(activeLayoutStorageKey));
  }, [activeLayoutStorageKey]);

  useEffect(() => {
    setShowUnconnectedEntities(false);
    setDetailLevel(defaultGraphDetailLevel(model.nodes.length));
  }, [model.nodes.length]);

  useEffect(() => {
    setIsSummaryCollapsed(model.nodes.length >= 24);
  }, [model.nodes.length]);

  useEffect(() => {
    setIsRelationshipNavigatorCollapsed(model.edges.length >= 24);
  }, [model.edges.length]);

  const activeJoinId = selection?.kind === 'join' ? selection.id : hoveredJoinId;
  const activeJoin = activeJoinId ? model.edges.find((edge) => edge.joinId === activeJoinId) : null;
  const activeNodeIds = useMemo(
    () => new Set(activeJoin ? [activeJoin.source, activeJoin.target] : []),
    [activeJoin],
  );
  const hasManualLayout = Object.keys(manualPositions).some((nodeId) =>
    graphProjection.nodes.some((node) => node.id === nodeId),
  );
  const generatedLayout = useMemo(
    () => layoutOntologyGraph(
      graphProjection.nodes,
      graphProjection.edges,
      detailLevel === 'overview'
        ? 'overview'
        : detailLevel === 'entities'
          ? 'condensed'
          : 'comfortable',
    ),
    [detailLevel, graphProjection.edges, graphProjection.nodes],
  );

  const computedNodes = useMemo<Node<OntologyNodeData>[]>(
    () =>
      graphProjection.nodes.map((node) => {
        const position = manualPositions[node.id] ?? generatedLayout[node.id] ?? node.position;
        return {
          id: node.id,
          type: 'ontologyEntity',
          position,
          sourcePosition: Position.Right,
          targetPosition: Position.Left,
          data: {
            ...node,
            position,
            detailLevel,
            isSelected: selection?.kind === 'entity' && selection.id === node.entityId,
            isConnectedToSelection: activeNodeIds.has(node.id),
            isDimmed: Boolean(activeJoin && !activeNodeIds.has(node.id)),
            selectedMetricId: selection?.kind === 'metric' ? selection.id : undefined,
            onSelectMetric: (metricId: string) => onSelect({ kind: 'metric', id: metricId }),
            onSelectBusinessMeaning: (businessMeaningId: string) => onSelect({ kind: 'businessMeaning', id: businessMeaningId }),
          },
        };
      }),
    [activeJoin, activeNodeIds, detailLevel, generatedLayout, graphProjection.nodes, manualPositions, onSelect, selection],
  );
  const [nodes, setNodes, onNodesChange] = useNodesState<OntologyNodeData>(computedNodes);

  useEffect(() => {
    setNodes(computedNodes);
  }, [computedNodes, setNodes]);

  const edges = useMemo<Edge[]>(
    () =>
      graphProjection.edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        type: 'ontologyRelationship',
        selected: selection?.kind === 'join' && selection.id === edge.joinId,
        animated: selection?.kind === 'join' && selection.id === edge.joinId,
        interactionWidth: 28,
        markerEnd: { type: MarkerType.ArrowClosed },
        data: {
          ...edge,
          isSelected: selection?.kind === 'join' && selection.id === edge.joinId,
          isHovered: hoveredJoinId === edge.joinId,
          isDimmed: Boolean(activeJoinId && activeJoinId !== edge.joinId),
          isOverview: detailLevel !== 'details',
          showLabel: detailLevel === 'details',
        } satisfies OntologyEdgeData,
        style: {
          stroke: selection?.kind === 'join' && selection.id === edge.joinId ? '#C53D3D' : '#8B95A1',
          strokeWidth: selection?.kind === 'join' && selection.id === edge.joinId ? 3 : 2,
        },
      })),
    [activeJoinId, detailLevel, graphProjection.edges, hoveredJoinId, selection],
  );

  const handleConnect = (connection: Connection) => {
    const source = connection.source?.replace(/^entity-/, '');
    const target = connection.target?.replace(/^entity-/, '');
    if (!source || !target || source === target) return;
    onCreateJoinBetweenEntities?.(source, target);
  };

  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      onNodesChange(changes);
    },
    [onNodesChange],
  );

  const persistManualPosition = useCallback(
    (node: Node<OntologyNodeData>) => {
      setManualPositions((current) => {
        const next = {
          ...current,
          [node.id]: { x: Math.round(node.position.x), y: Math.round(node.position.y) },
        };
        writeStoredLayout(activeLayoutStorageKey, next);
        return next;
      });
    },
    [activeLayoutStorageKey],
  );

  const resetLayout = useCallback(() => {
    setManualPositions({});
    writeStoredLayout(activeLayoutStorageKey, {});
    setNodes(
      computedNodes.map((node) => ({
        ...node,
        position: generatedLayout[node.id] ?? node.position,
      })),
    );
    requestAnimationFrame(() => {
      void flowInstance?.fitView({ padding: 0.18, duration: 300 });
    });
  }, [activeLayoutStorageKey, computedNodes, flowInstance, generatedLayout, setNodes]);

  if (model.nodes.length === 0) {
    return (
      <Card className="flex h-full min-h-[640px] items-center justify-center rounded-none border-0 bg-gray-50 p-8 shadow-none dark:bg-dark-bg/30">
        <div className="max-w-2xl text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-gray-200 bg-white text-gray-500 shadow-sm dark:border-dark-border dark:bg-dark-surface">
            <Database className="h-7 w-7" />
          </div>
          <h3 className="mt-4 font-subtitle text-2xl text-charcoal dark:text-gray-100">
            Start your workspace ontology
          </h3>
          <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-gray-500 dark:text-gray-400">
            Create governed business entities from selected warehouse scope, then add metrics and joins before review.
          </p>
          <>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {onCreateEntity && (
                  <button
                    type="button"
                    onClick={onCreateEntity}
                    className="inline-flex h-9 items-center gap-2 rounded-full bg-eliza-red px-4 text-sm font-medium text-white shadow-sm transition-colors hover:bg-eliza-red-light"
                  >
                    <Plus className="h-4 w-4" />
                    Create entity
                  </button>
                )}
                {onCreateMetric && (
                  <button
                    type="button"
                    onClick={onCreateMetric}
                    className="inline-flex h-9 items-center gap-2 rounded-full border border-gray-200 bg-white px-4 text-sm font-medium text-charcoal transition-colors hover:bg-gray-50 dark:border-dark-border dark:bg-dark-surface dark:text-gray-100"
                  >
                    <Plus className="h-4 w-4" />
                    Metric
                  </button>
                )}
                {onCreateJoin && (
                  <button
                    type="button"
                    onClick={onCreateJoin}
                    className="inline-flex h-9 items-center gap-2 rounded-full border border-gray-200 bg-white px-4 text-sm font-medium text-charcoal transition-colors hover:bg-gray-50 dark:border-dark-border dark:bg-dark-surface dark:text-gray-100"
                  >
                    <Plus className="h-4 w-4" />
                    Join
                  </button>
                )}
              </div>

              <div className="mt-6 rounded-2xl border border-dashed border-gray-300 bg-white/70 p-4 text-left dark:border-dark-border dark:bg-dark-surface/70">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
                  <Bot className="h-4 w-4" />
                  AI-assisted creation
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <CanvasAiPlaceholder icon={WandSparkles} label="Draft ontology with AI" />
                  <CanvasAiPlaceholder icon={Sparkles} label="Suggest entities from tables" />
                  <CanvasAiPlaceholder icon={Link2} label="Infer joins from schema" />
                  <CanvasAiPlaceholder icon={Sparkles} label="Generate metrics from questions" />
                </div>
              </div>
          </>
        </div>
      </Card>
    );
  }

  return (
    <div className="relative h-full min-h-[640px] overflow-hidden bg-gray-50 dark:bg-dark-bg/30" data-testid="ontology-reactflow-canvas">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        nodesDraggable
        elementsSelectable
        fitView
        fitViewOptions={{ padding: 0.18 }}
        minZoom={0.25}
        maxZoom={1.5}
        nodesConnectable={Boolean(onCreateJoinBetweenEntities)}
        connectOnClick={false}
        edgesFocusable
        nodesFocusable
        proOptions={{ hideAttribution: true }}
        onConnect={handleConnect}
        onInit={setFlowInstance}
        onNodesChange={handleNodesChange}
        onNodeDragStop={(_, node) => persistManualPosition(node as Node<OntologyNodeData>)}
        onNodeClick={(_, node) => {
          onSelect({ kind: 'entity', id: node.data.entityId });
          if (detailLevel === 'overview' && (node.data.groupedEntityCount ?? 1) > 1) {
            setDetailLevel('entities');
          }
        }}
        onEdgeClick={(_, edge) => onSelect({ kind: 'join', id: (edge.data as { joinId: string }).joinId })}
        onEdgeMouseEnter={(_, edge) => setHoveredJoinId((edge.data as { joinId: string }).joinId)}
        onEdgeMouseLeave={() => setHoveredJoinId(null)}
      >
        <Background color="#E5E7EB" gap={18} />
        <Controls className="!border-gray-200 !bg-white [&_button]:!border-gray-200 [&_button]:!bg-white [&_button]:!text-gray-600" />
        <MiniMap
          pannable
          zoomable
          nodeColor={(node) => (node.data.isSelected ? '#C53D3D' : '#E5E7EB')}
          maskColor="rgba(17, 24, 39, 0.08)"
          className="!border-gray-200 !bg-white"
        />
      </ReactFlow>

      <div
        className="pointer-events-auto absolute left-1/2 top-4 flex -translate-x-1/2 items-center gap-1 rounded-full border border-gray-200 bg-white/95 p-1 shadow-sm backdrop-blur dark:border-dark-border dark:bg-dark-surface/95"
        role="group"
        aria-label="Ontology map detail level"
        data-testid="ontology-map-detail-level"
      >
        {GRAPH_DETAIL_LEVELS.map((level) => (
          <Button
            key={level.value}
            type="button"
            variant={detailLevel === level.value ? 'default' : 'ghost'}
            size="sm"
            aria-pressed={detailLevel === level.value}
            title={level.description}
            onClick={() => {
              setDetailLevel(level.value);
              if (level.value === 'details') {
                setIsSummaryCollapsed(true);
              }
            }}
            className="h-7 px-3"
          >
            {level.label}
          </Button>
        ))}
      </div>

      {isSummaryCollapsed ? (
        <button
          type="button"
          onClick={() => setIsSummaryCollapsed(false)}
          className="pointer-events-auto absolute left-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white/95 text-gray-600 shadow-sm backdrop-blur transition-colors hover:bg-gray-50 dark:border-dark-border dark:bg-dark-surface/95 dark:text-gray-300"
          aria-label="Expand graph summary"
          title="Expand graph summary"
        >
          <Link2 className="h-4 w-4" />
          <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-gray-100 px-1 text-[10px] font-semibold text-gray-600 dark:bg-dark-surface-2 dark:text-gray-300">
            {visibleGraphNodes.length}
          </span>
        </button>
      ) : (
        <div className="pointer-events-none absolute left-4 top-4 w-[360px] max-w-[calc(100%-2rem)] rounded-lg border border-gray-200 bg-white/90 px-3 py-2 shadow-sm backdrop-blur dark:border-dark-border dark:bg-dark-surface/90">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-start gap-2 text-xs font-semibold leading-5 text-charcoal dark:text-gray-100">
              <Link2 className="h-3.5 w-3.5 text-gray-500" />
              {canFocusConnectedGraph
                ? detailLevel === 'overview'
                  ? `${graphProjection.nodes.length} connected sources shown, ${model.summary.entities} concepts total`
                  : `${visibleGraphNodes.length} connected entities shown, ${model.summary.entities} total`
                : `${model.summary.entities} entities`}
              , {model.summary.semanticMappings} business meanings, {model.summary.metrics} metrics, {model.summary.joins} joins
            </div>
            <button
              type="button"
              onClick={() => setIsSummaryCollapsed(true)}
              className="pointer-events-auto inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-dark-surface-2 dark:hover:text-gray-300"
              aria-label="Collapse graph summary"
              title="Collapse graph summary"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={resetLayout}
              title={hasManualLayout ? 'Reset positions and fit the graph' : 'Fit the generated layout'}
              className="pointer-events-auto px-2.5"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Auto-layout
            </Button>
            {canFocusConnectedGraph ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                aria-pressed={showUnconnectedEntities}
                onClick={() => setShowUnconnectedEntities((current) => !current)}
                className="pointer-events-auto px-2.5"
              >
                <GitBranch className="h-3.5 w-3.5" />
                {showUnconnectedEntities ? 'Hide' : 'Show'} {unconnectedEntityCount} unconnected
              </Button>
            ) : null}
          </div>
          <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
            {detailLevel === 'overview'
              ? 'Overview groups logical concepts by physical source. Use the map detail control to reveal entities.'
              : detailLevel === 'entities'
                ? 'Entity view adds physical sources and semantic counts.'
                : 'Detail view shows descriptions, business meanings, metrics, and relationship labels.'}
          </p>
          {unconnectedEntityCount > 0 ? (
            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              {canFocusConnectedGraph && !showUnconnectedEntities
                ? `${unconnectedEntityCount} unconnected entit${unconnectedEntityCount === 1 ? 'y is' : 'ies are'} hidden to keep the core graph readable.`
                : `${unconnectedEntityCount} unconnected entit${unconnectedEntityCount === 1 ? 'y is' : 'ies are'} shown in separate lanes.`}
            </p>
          ) : null}
          <p className="mt-2 max-w-[250px] border-t border-gray-100 pt-2 text-[11px] leading-4 text-gray-500 dark:border-dark-border dark:text-gray-400">
            Ontology mode shows logical relationships. SQL evidence is shown in relationship details.
          </p>
        </div>
      )}

      {model.edges.length > 0 && (
        <RelationshipNavigator
          edges={model.edges}
          selectedJoinId={selection?.kind === 'join' ? selection.id : null}
          hoveredJoinId={hoveredJoinId}
          collapsed={isRelationshipNavigatorCollapsed}
          onCollapsedChange={setIsRelationshipNavigatorCollapsed}
          onSelect={(joinId) => onSelect({ kind: 'join', id: joinId })}
          onHover={setHoveredJoinId}
        />
      )}
    </div>
  );
}

function RelationshipNavigator({
  edges,
  selectedJoinId,
  hoveredJoinId,
  collapsed,
  onCollapsedChange,
  onSelect,
  onHover,
}: {
  edges: OntologyGraphEdge[];
  selectedJoinId: string | null;
  hoveredJoinId: string | null;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  onSelect: (joinId: string) => void;
  onHover: (joinId: string | null) => void;
}) {
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => onCollapsedChange(false)}
        className="pointer-events-auto absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white/95 text-gray-600 shadow-sm backdrop-blur transition-colors hover:bg-gray-50 dark:border-dark-border dark:bg-dark-surface/95 dark:text-gray-300"
        aria-label="Expand relationships"
        title="Expand relationships"
      >
        <GitBranch className="h-4 w-4" />
        <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-gray-100 px-1 text-[10px] font-semibold text-gray-600 dark:bg-dark-surface-2 dark:text-gray-300">
          {edges.length}
        </span>
      </button>
    );
  }

  return (
    <div
      className="pointer-events-auto absolute right-4 top-4 w-[320px] rounded-lg border border-gray-200 bg-white/95 p-3 shadow-sm backdrop-blur dark:border-dark-border dark:bg-dark-surface/95"
      data-testid="relationship-navigator"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-charcoal dark:text-gray-100">
          <GitBranch className="h-3.5 w-3.5 text-gray-500" />
          Relationships
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{edges.length}</Badge>
          <button
            type="button"
            onClick={() => onCollapsedChange(true)}
            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-dark-surface-2 dark:hover:text-gray-300"
            aria-label="Collapse relationships"
            title="Collapse relationships"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="mt-2 max-h-48 space-y-1 overflow-y-auto pr-1">
        {edges.map((edge) => (
          <button
            key={edge.joinId}
            type="button"
            aria-pressed={selectedJoinId === edge.joinId || hoveredJoinId === edge.joinId}
            onClick={() => onSelect(edge.joinId)}
            onMouseEnter={() => onHover(edge.joinId)}
            onMouseLeave={() => onHover(null)}
            title={edge.joinCondition || edge.description || edge.label}
            className={cn(
              'block w-full rounded-md border px-2.5 py-2 text-left transition-colors',
              selectedJoinId === edge.joinId || hoveredJoinId === edge.joinId
                ? 'border-eliza-red bg-eliza-red/5'
                : 'border-transparent hover:border-gray-200 hover:bg-gray-50 dark:hover:border-dark-border dark:hover:bg-dark-surface-2',
            )}
          >
            <span className="block truncate text-xs font-semibold text-charcoal dark:text-gray-100">
              {edge.label}
            </span>
            <span className="mt-0.5 block truncate text-[10px] text-gray-500 dark:text-gray-400">
              {edge.routeLabel}
            </span>
            <span className="mt-2 flex flex-wrap gap-1">
              <RelationshipChip>{edge.cardinalityLabel}</RelationshipChip>
              <RelationshipChip tone={edge.traversalAllowed ? 'success' : edge.relationshipKind === 'semantic_only' ? 'neutral' : 'warning'}>
                {edge.joinEvidenceStatusLabel}
              </RelationshipChip>
              <RelationshipChip tone={edge.reviewStatusTone}>{edge.reviewStatusLabel}</RelationshipChip>
            </span>
            <span className="mt-2 block truncate font-mono text-[10px] text-gray-500 dark:text-gray-400">
              Join evidence: {edge.joinEvidenceLabel}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function readStoredLayout(storageKey?: string): StoredLayout {
  if (!storageKey || typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).flatMap(([nodeId, value]) => {
        if (!value || typeof value !== 'object') return [];
        const position = value as Partial<XYPosition>;
        if (typeof position.x !== 'number' || typeof position.y !== 'number') return [];
        return [[nodeId, { x: position.x, y: position.y }]];
      }),
    );
  } catch {
    return {};
  }
}

function writeStoredLayout(storageKey: string | undefined, layout: StoredLayout) {
  if (!storageKey || typeof window === 'undefined') return;
  try {
    if (Object.keys(layout).length === 0) {
      window.localStorage.removeItem(storageKey);
      return;
    }
    window.localStorage.setItem(storageKey, JSON.stringify(layout));
  } catch {
    // Browser storage is a convenience cache; draft saves must not fail because it is unavailable.
  }
}

function CanvasAiPlaceholder({
  icon: Icon,
  label,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <button
      type="button"
      disabled
      title="Coming soon"
      className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-400 dark:border-dark-border dark:bg-dark-surface"
    >
      <span className="inline-flex items-center gap-2">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </span>
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-dark-surface-2 dark:text-gray-400">
        Coming soon
      </span>
    </button>
  );
}
